"use client";

import LoginDialog from "@/components/dialogs/login-dialog";
import { Toaster } from "@/components/ui/sonner";
import StytchProvider from "@/shared/auth/stytch-provider";
import { StytchMemberSessionGuard } from "@/shared/hooks/use-stytch-member-guard";
import { LayoutPreferenceContext } from "@/shared/providers/preferences-provider";
import { UserProvider } from "@/shared/providers/user-provider";
import { ThemeProvider } from "next-themes";
import { type ReactNode, useEffect, useState } from "react";
import { IntercomProvider } from "react-use-intercom";
import { GlobalContextProvider } from "./use-global";

export const Providers = ({
  children,
  layoutPreferences,
}: {
  children: ReactNode;
  // biome-ignore lint: no-any
  layoutPreferences: any;
}) => {
  // Iframe local state to determine if the app is running in an iframe
  const [isIframeLocal, setIsIframeLocal] = useState<boolean | null>(null);

  useEffect(() => {
    try {
      // Check if the current window is not the top window
      setIsIframeLocal(window.self !== window.top);
    } catch (_error) {
      // If an error occurs (likely due to cross-origin restrictions), assume it is in an iframe
      setIsIframeLocal(true);
    }
  }, []);

  return (
    <IntercomProvider appId={process.env.NEXT_PUBLIC_INTERCOM_APP_ID || ""}>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
        forcedTheme={
          isIframeLocal || isIframeLocal === null ? "light" : undefined
        }
      >
        <LayoutPreferenceContext.Provider value={{ layoutPreferences }}>
          <StytchProvider>
            <StytchMemberSessionGuard />
            <UserProvider>
              <GlobalContextProvider>
                {children}
                <LoginDialog />
              </GlobalContextProvider>
            </UserProvider>
          </StytchProvider>
        </LayoutPreferenceContext.Provider>
        <Toaster richColors />
      </ThemeProvider>
    </IntercomProvider>
  );
};
